import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { debounceTime, Observable, Subscription } from 'rxjs';

@Directive({
  selector: '[appObserveIntersection]',
  exportAs: 'intersectionObserver',
})
export class ObserveIntersectionDirective implements OnInit, OnDestroy {
  @Input() debounceTime = 0;
  @Input() isContinuous = false;
  @Input() targetRatio?: number;
  @Input() root?: Element | Document | null;
  @Input() rootMargin?: string;
  @Input() threshold?: number | number[] = 1;

  @Output() isIntersecting = new EventEmitter<boolean>();

  subscription: Subscription;

  constructor(private readonly element: ElementRef) {}

  ngOnInit() {
    this.subscription = this.createAndObserve();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createAndObserve() {
    const options: IntersectionObserverInit = {
      root: this.root,
      rootMargin: this.rootMargin,
      threshold: this.threshold,
    };

    return new Observable<boolean>((subscriber) => {
      const intersectionObserver = new IntersectionObserver(([entry]) => {
        let isIntersecting: boolean;
        if (this.targetRatio) {
          isIntersecting = this.targetRatio <= entry.intersectionRatio;
        } else {
          ({ isIntersecting } = entry);
        }
        subscriber.next(isIntersecting);

        if (isIntersecting && !this.isContinuous) {
          intersectionObserver.disconnect();
        }
      }, options);

      intersectionObserver.observe(this.element.nativeElement);

      return {
        unsubscribe() {
          intersectionObserver.disconnect();
        },
      };
    })
      .pipe(debounceTime(this.debounceTime))
      .subscribe((status) => {
        this.isIntersecting.emit(status);
      });
  }
}
